import { readCookie } from './readCookie';

// OneTrust's scripts set this global variable after they load.
declare global {
  interface Window {
    OnetrustActiveGroups?: string;
  }
}

// These cookie category codes are provided by OneTrust
// and map onto corresponding category names.
export type CookieCategoryCode = 'C0001' | 'C0002' | 'C0004';

export type CookieCategoryName = 'essential' | 'analytics' | 'marketing';

type CategoryNameToCodeMap = {
  [key in CookieCategoryName]: CookieCategoryCode;
};

type ActiveCategoriesByName = {
  [key in CookieCategoryName]: boolean;
};

const categoryCodes: Array<CookieCategoryCode> = ['C0001', 'C0002', 'C0004'];

const categoryNameToCodeMap: CategoryNameToCodeMap = {
  essential: 'C0001',
  analytics: 'C0002',
  marketing: 'C0004',
};

type CategoryCodeToNameMap = {
  [key in CookieCategoryCode]: CookieCategoryName;
};

type ActiveCategoriesByCode = {
  [key in CookieCategoryCode]: boolean;
};

const categoryCodeToNameMap = Object.entries(categoryNameToCodeMap).reduce(
  (accum, entry) => {
    // Didn't destructure these because using `as` worked better for typing.
    const name = entry[0] as CookieCategoryName;
    const code = entry[1] as CookieCategoryCode;
    accum[code] = name;
    return accum;
  },
  {} as CategoryCodeToNameMap
);

// Read consent values from `OptanonConsent` cookie. This is used as
// a fallback for when the `OnetrustActiveGroups` global variable
// doesn't exist yet because the scripts that create it haven't
// finished loading and executing. The benefit of using the `OptanonConsent`
// cookie as a fallback is that it's available immediately on page
// load once it's been set at least once. This means that on subsequent
// page loads the consented categories can be read even when `OnetrustActiveGroups`
// doesn't exist yet, which is useful for some app-level code that only
// executes a single time on boot and therefore can't react to a hook
// like `useCookieConsent` which would occur long after the app's initial boot.
const readCookieCategoriesFromCookie = () => {
  const optanonConsent = readCookie('OptanonConsent');
  const groups = new URLSearchParams(optanonConsent).get('groups') || '';
  const activeCodes = groups
    .split(',')
    .map((group) => group.split(':'))
    // Filter out non-existent groups, which occurs when `OptanonConsent`
    // hasn't yet been set.
    .filter(([groupCode]) => !!groupCode)
    // Filter out falsey groups that the user hasn't consented to.
    .filter(([, isConsented]) => isConsented === '1')
    .map(([groupCode]) => groupCode);
  return activeCodes;
};

// Name of the DOM event that the cookie-consent script (in the sibling
// `@zapier/cookie-consent` package) dispatches on `window` whenever the
// user's consent state resolves or changes. Lifted into a shared constant
// so `writeCookie` and `useCookieConsent` can't drift apart.
export const COOKIE_CONSENT_EVENT = 'zapier.cookie-consent';

// True when consent state can be answered now: either OneTrust has set
// `OnetrustActiveGroups` to a non-empty string, or the persisted
// `OptanonConsent` cookie tells us what the user chose on a prior session.
// False during the OneTrust loading window on first-page visits, the one
// that produced INC-956.
//
// Empty-string `OnetrustActiveGroups` is deliberately NOT treated as ready.
// OneTrust's docs say the global is "re-populated on every page load once
// the script is executed" but don't rule out a transient empty-string before
// consent resolves. Per OneTrust's docs, `OptanonConsent` is written by
// `OTSDKstub.js` before the consent event fires, so legitimate "loaded, no
// consent granted" users still resolve via the cookie check below without
// the empty-string fast-path.
export const isConsentReady = (): boolean => {
  // Match the SSR guard in `waitForConsentReady` so server renders don't
  // log a noisy `console.error` on every initial mount of `useCookieConsent`.
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (window.OnetrustActiveGroups) {
      return true;
    }
  } catch (error) {
    console.error('Failed to access window.OnetrustActiveGroups:', error);
    return false;
  }
  return !!readCookie('OptanonConsent');
};

export const getActiveCategoryCodes = () => {
  let activeGroups = '';

  try {
    activeGroups = window.OnetrustActiveGroups || '';
  } catch (error) {
    console.error('Failed to access window.OnetrustActiveGroups:', error);
  }

  // "Live" codes from the `OnetrustActiveGroups` global variable.
  const liveActiveCodes = activeGroups.split(/\s*,\s*/).filter(Boolean);
  // See note at `readCookieCategoriesFromCookie` definition.
  const activeCodes =
    liveActiveCodes.length > 0
      ? liveActiveCodes
      : readCookieCategoriesFromCookie();
  // Use `Set` to dedupe array, being sure that C0001 (essential category)
  // always exists within it.
  return [
    ...new Set(['C0001', ...activeCodes]),
  ].sort() as Array<CookieCategoryCode>;
};

export const readCookieCategories = () => {
  const activeCodes = getActiveCategoryCodes();
  const byCode: ActiveCategoriesByCode = categoryCodes.reduce(
    (accum, catCode) => {
      accum[catCode] = activeCodes.includes(catCode);
      return accum;
    },
    {} as ActiveCategoriesByCode
  );
  const byName: ActiveCategoriesByName = Object.entries(byCode).reduce(
    (accum, [categoryCode, isActive]) => {
      const categoryName =
        categoryCodeToNameMap[categoryCode as CookieCategoryCode];
      accum[categoryName] = isActive;
      return accum;
    },
    {} as ActiveCategoriesByName
  );
  return { byCode, byName };
};

// Indicates whether the cookie categorized as `category` can be
// written based on user consent.
export const canWriteCookieCategory = (category: CookieCategoryName) => {
  // In case `OnetrustActiveGroups` hasn't been set yet, still allow
  // essential cookies to be set.
  if (category === 'essential') {
    return true;
  }
  const cookieCategories = readCookieCategories();
  return !!cookieCategories.byName[category];
};
